import {selectMessages} from '../../components/selectMessages'
import {PageTitle} from '../../_metronic/layout/core'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import moment from 'moment'
import {EducationDatePicker} from '../../components/EducationDatePicker'
import {
  DetailQueryState,
  homeworkInitial,
  homeworkValidations,
  IHomework,
  LANGUAGE_OPTIONS,
  reduxProps,
} from '../../constants'
import {actionCreateHomework, actionGetCurriculumById, actionGetCurriculums} from '../../redux/education'
import {actionGetCategories, actionGetStories} from '../../redux/education/actions/story.action'
import {actionDeleteModalOpen} from '../../redux/modal'
import {StoryList} from '../story/story-list'
import {storyCoverUrl} from '../story/story-item'
import {UiIcon} from '../../components/UiIcon'
import {toast} from 'react-toastify'
import {useFormik} from 'formik'
import Select from '../../components/EducationSelect'
import './Create.scss'

export function HomeworkCreate() {
  const navigate = useNavigate()
  const [selectedStoryIds, setSelectedStoryIds] = useState<number[]>([])
  const {stories, classroom} = useSelector((state: reduxProps) => state.educationReducer)
  const {staffInfo} = useSelector((state: reduxProps) => state.authReducer)
  let {id} = useParams<DetailQueryState>()
  const selectedStories = useMemo(() => stories.filter(item => selectedStoryIds.includes(item.storyId)), [stories, selectedStoryIds])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const savingRef = useRef(false)

  useEffect(() => {
    id && getCurriculum()
  }, [])

  const dispatch = useDispatch()

  const handleDateChange = (date: null | Date, fieldName: any) => {
    if (date) setFieldValue(fieldName, moment(date).utc().toDate())
  }
  const {values, setFieldValue} = useFormik({
    initialValues: homeworkInitial,
    validationSchema: homeworkValidations,
    enableReinitialize: true,
    onSubmit: (values: IHomework) => {},
  })

  const handleSetSelectedStoryId = (item: number) => {
    if (savingRef.current) return
    setSaveError('')
    if (selectedStoryIds.indexOf(item) === -1) {
      let newStories: number[] = [...selectedStoryIds]
      newStories.push(item)
      setSelectedStoryIds(newStories)
    } else {
      let newStories = selectedStoryIds.filter((storyItem) => storyItem !== item)
      setSelectedStoryIds(newStories)
    }
  }
  const getCurriculum = () => {
    dispatch(
      actionGetCurriculumById(id, (r) => {
        if (r.type === 'SUCCESS') {
          setSelectedStoryIds((r?.payload?.data?.curriculumDetail || []).map((item: any) => typeof item === 'number' ? item : item.storyId))
        }
      })
    )
  }

  async function createHomework() {
    if (savingRef.current) return
    if (!selectedStories.length) {setSaveError('En az bir kitap seçin.'); return}
    if (!classroom.id) {setSaveError('Önce bir sınıf seçin.'); return}
    if (!values.startDate || !values.endDate || new Date(values.endDate) < new Date(values.startDate)) {
      setSaveError('Bitiş tarihi başlangıç tarihinden önce olamaz.'); return
    }
    savingRef.current = true
    setSaving(true)
    setSaveError('')
    try {
      const results = await Promise.all(selectedStories.map(item => new Promise<{id: number; success: boolean}>(resolve => {
        dispatch(actionCreateHomework({...values, storyId: item.storyId,
          organizationId: staffInfo.organizationId, classroomId: classroom.id, schoolId: classroom.schoolId,
          isReadAloud: !values.requestVoiceRecording && values.isReadAloud}, result => resolve({id: item.storyId, success: result.type === 'SUCCESS'})))
      })))
      const failed = results.filter(item => !item.success).map(item => item.id)
      if (!failed.length) {
        toast.success(`${results.length} kitap için sorumluluk oluşturuldu.`)
        navigate('/homework/')
      } else {
        setSelectedStoryIds(failed)
        setSaveError(`${results.length - failed.length} sorumluluk oluşturuldu. ${failed.length} kitap kaydedilemedi; seçili kitaplar için tekrar deneyebilirsiniz.`)
      }
    } finally {savingRef.current = false; setSaving(false)}
  }

  const applyLanguageChange = (languageCode: string) => {
    setFieldValue('languageCode', languageCode)
    setSelectedStoryIds([])
    dispatch(actionGetStories({languageCode}))
    dispatch(actionGetCurriculums({languageCode, createdUserId: staffInfo.id}))
    dispatch(actionGetCategories({languageCode, categoryTypeId: 4}))
  }

  const handleLanguageChange = (languageCode: string) => {
    if (languageCode === values.languageCode) return

    if (selectedStoryIds.length === 0) {
      applyLanguageChange(languageCode)
      return
    }

    dispatch(
      actionDeleteModalOpen({
        deleteModalHandle: () => applyLanguageChange(languageCode),
        title: '',
        buttonText: 'Devam Et',
        contentId: 0,
        content:
          'Dil değiştirildiğinde mevcut kitap seçimleriniz kaldırılacaktır. Devam etmek istiyor musunuz?',
      })
    )
  }

  return (
    <div id='homeWorkAdd'>
      <PageTitle description='Kitapları seçin, okuma seçeneklerini belirleyin ve sınıfınıza atayın.'>Sorumluluk oluştur</PageTitle>
      <div className='edu-assignment-workspace'>
        <aside className='edu-assignment-settings'>
          <div className='edu-assignment-scroll' tabIndex={0} role='region' aria-label='Sorumluluk oluşturma paneli'>
          <fieldset disabled={saving}>
          <section id='homeworkForm' aria-labelledby='homeworkSettingsTitle'>
            <div className='homework-date-fields'>
              <div className='formGroup'>
                <label htmlFor='homeworkStartDate'>Başlangıç tarihi</label>
                <EducationDatePicker
                  id='homeworkStartDate'
                  disabled={saving}
                  maxDate={new Date(values.endDate)}
                  selected={moment(values.startDate).utc(false).toDate()}
                  onChange={(date) => handleDateChange(date, 'startDate')}
                />
              </div>
              <div className='formGroup'>
                <label htmlFor='homeworkEndDate'>Bitiş tarihi</label>
                <EducationDatePicker
                  id='homeworkEndDate'
                  disabled={saving}
                  minDate={new Date(values.startDate)}
                  selected={moment(values.endDate).utc(false).toDate()}
                  onChange={(date) => handleDateChange(date, 'endDate')}
                />
              </div>
            </div>
            <div className='formGroup homework-language-field'>
              <label htmlFor='languageCode'>Kitap dili</label>
              <Select {...selectMessages}
                isDisabled={saving}
                className='kidly-select'
                classNamePrefix='kidly'
                inputId='languageCode'
                name='languageCode'
                options={LANGUAGE_OPTIONS}
                value={LANGUAGE_OPTIONS.find((item) => item.value === values.languageCode)}
                onChange={(item: any) => item && handleLanguageChange(item.value)}
              />
            </div>
            <div className='homework-audio-options'>
              <label className='homework-option' htmlFor='homeworkReadAloud'>
                <span className='homework-option-copy'>
                  <span className='homework-option-title'>Sesli dinleme</span>
                  <span className='homework-option-description'>{values.requestVoiceRecording ? 'Ses kaydı istendiği için kapalı.' : 'Kitabın sesli anlatımı açık olsun.'}</span>
                </span>
                <input id='homeworkReadAloud' type='checkbox' role='switch'
                  checked={!values.requestVoiceRecording && values.isReadAloud === true}
                  disabled={values.requestVoiceRecording}
                  onChange={(event) => setFieldValue('isReadAloud', event.target.checked)}
                  aria-label='Sesli dinleme' />
                <span className='homework-switch' aria-hidden='true' />
              </label>
              <label className='homework-option' htmlFor='homeworkRequestRecording'>
                <span className='homework-option-copy'>
                  <span className='homework-option-title'>Ses kaydı istensin mi?</span>
                  <span className='homework-option-description'>Öğrenci okumasını kaydedip göndersin.</span>
                </span>
                <input id='homeworkRequestRecording' type='checkbox' role='switch'
                  checked={values.requestVoiceRecording}
                  onChange={(event) => {
                    setFieldValue('requestVoiceRecording', event.target.checked)
                    if (event.target.checked) setFieldValue('isReadAloud', false)
                  }}
                  aria-label='Ses kaydı istensin mi?' />
                <span className='homework-switch' aria-hidden='true' />
              </label>
            </div>
          </section>

          <section className='edu-selected-panel' aria-labelledby='selectedBooksTitle'>
            <div className='edu-selected-heading'><h2 id='selectedBooksTitle'>Seçilen kitaplar</h2><span>{selectedStories.length}</span></div>
            {selectedStories.length ? <ul className='edu-selected-list'>
              {selectedStories.map(item => <li key={item.storyId}>
                <img src={storyCoverUrl(item)} alt='' onError={event => {event.currentTarget.style.visibility = 'hidden'}}/>
                <span>{item.title}</span>
                <button type='button' aria-label={`${item.title}: seçimi kaldır`} onClick={() => handleSetSelectedStoryId(item.storyId)}><UiIcon name='close' size={16}/></button>
              </li>)}
            </ul> : <div className='edu-selected-empty'><UiIcon name='book' size={25}/><p>Listeden kitap seçerek başlayın.</p></div>}
          </section>
          </fieldset>
          <div className='edu-assignment-submit'>
            {saveError && <div role='alert' className='alert alert-danger'>{saveError}</div>}
            <button type='button' className='btn btn-primary' disabled={!selectedStories.length || saving || !classroom.id} onClick={createHomework} aria-busy={saving}>
              {saving ? 'Oluşturuluyor…' : `Sorumluluk oluştur${selectedStories.length ? ` (${selectedStories.length})` : ''}`}
            </button>
            <p>Her kitap için ayrı bir sorumluluk oluşturulur.</p>
          </div>
          </div>
        </aside>
        <div className='edu-assignment-catalog' aria-busy={saving}>
          <StoryList infiniteScroll disabled={saving} key={values.languageCode} handleSetSelectedStoryId={handleSetSelectedStoryId}
            selectedStoryIds={selectedStoryIds} showCurriculumDropdown={true}
            description='Kitapları inceleyin ve sınıfınız için seçin.' />
        </div>
      </div>
    </div>
  )
}
